import { z } from 'zod';

// ---- Location Detail (details-filters response) ----

export const locationDetailSchema = z
    .object({
        id: z.number().int(),
        name: z.string(),
        locationKey: z.string().optional(),
        abbreviation: z.string().optional().nullable(),
        brandName: z.string().optional().nullable(),
        areaName: z.string().optional().nullable(),
        continentName: z.string().optional().nullable(),
        unit: z.string().optional().nullable(),
        managementCompanyName: z.string().optional().nullable(),
        regionName: z.string().optional().nullable(),
        typeName: z.string().optional().nullable(),
    })
    .passthrough();

export const locationDetailsResponseSchema = z
    .object({
        location: locationDetailSchema,
    })
    .passthrough();

export type LocationDetail = z.infer<typeof locationDetailSchema>;
export type LocationDetailsResponse = z.infer<typeof locationDetailsResponseSchema>;

// ---- Dashboard Files (load-files response) ----

export const dashboardFileSchema = z
    .object({
        id: z.number().int(),
        isActive: z.boolean().optional().nullable(),
        fileName: z.string(),
        name: z.string(),
        description: z.string().optional().nullable(),
        extension: z.string(),
        fileStatusId: z.number().int(),
        uploadDate: z.coerce.date().optional().nullable(),
        disciplineName: z.string().optional().nullable(),
        projectId: z.number().int().optional().nullable(),
        projectName: z.string().optional().nullable(),
    })
    .passthrough();

const departmentSchema = z
    .object({
        files: z.array(dashboardFileSchema).default([]),
    })
    .passthrough();

const disciplineSchema = z
    .object({
        name: z.string().optional().nullable(),
        departments: z.array(departmentSchema).default([]),
    })
    .passthrough();

export const locationFilesResponseSchema = z
    .object({
        disciplines: z.array(disciplineSchema).default([]),
    })
    .passthrough();

export type DashboardFile = z.infer<typeof dashboardFileSchema> & {
    /** Injected during flattening — not on the raw API object */
    disciplineName: string;
};
export type LocationFilesResponse = z.infer<typeof locationFilesResponseSchema>;

// ---- Flatten helper ----

export const flattenFiles = (response: LocationFilesResponse): DashboardFile[] => {
    const flat: DashboardFile[] = [];
    for (const disc of response.disciplines) {
        const disciplineName: string = disc.name ?? '';
        for (const dept of disc.departments) {
            for (const f of dept.files) {
                flat.push({ ...f, disciplineName } as DashboardFile);
            }
        }
    }
    // Sort newest-first by uploadDate
    flat.sort((a, b) => {
        const da = a.uploadDate ? new Date(a.uploadDate).getTime() : 0;
        const db = b.uploadDate ? new Date(b.uploadDate).getTime() : 0;
        return db - da;
    });
    return flat;
};

// ---- Dashboard Links (load-links response) ----

export const dashboardLinkSchema = z
    .object({
        id: z.number().int(),
        source: z.enum(['task', 'resource']),
        locationTaskId: z.number().int().optional().nullable(),
        projectResourceId: z.number().int().optional().nullable(),
        name: z.string(),
        url: z.string(),
        ordinal: z.number(),
        isActive: z.boolean(),
    })
    .passthrough();

export const locationLinksResponseSchema = z
    .object({
        links: z.array(dashboardLinkSchema).default([]),
    })
    .passthrough();

export type DashboardLink = z.infer<typeof dashboardLinkSchema>;
export type LocationLinksResponse = z.infer<typeof locationLinksResponseSchema>;

// ---- Location Summary (used by overview-tab response) ----

export const locationSummarySchema = z
    .object({
        id: z.number().int(),
        name: z.string(),
        abbreviation: z.string().optional().nullable(),
        locationKey: z.string(),
    })
    .passthrough();

export type LocationSummary = z.infer<typeof locationSummarySchema>;

// ---- Pie Chart Datum (used by load-locations-by-project) ----

export const pieChartDatumSchema = z
    .object({
        id: z.union([z.string(), z.number()]),
        label: z.string().optional().nullable(),
        value: z.number(),
    })
    .passthrough();

export type PieChartDatum = z.infer<typeof pieChartDatumSchema>;

// ---- LocationsByProjects response ----

export const locationsByProjectsResponseSchema = z
    .object({
        brand: z.array(pieChartDatumSchema).default([]),
        area: z.array(pieChartDatumSchema).default([]),
        region: z.array(pieChartDatumSchema).default([]),
        type: z.array(pieChartDatumSchema).default([]),
        managementCompany: z.array(pieChartDatumSchema).default([]),
    })
    .passthrough();

export type LocationsByProjectsResponse = z.infer<typeof locationsByProjectsResponseSchema>;

// ---- Overview Tab response ----

export const overviewTabResponseSchema = z
    .object({
        locations: z.array(locationSummarySchema).default([]),
    })
    .passthrough();

export type OverviewTabResponse = z.infer<typeof overviewTabResponseSchema>;
